import { c as _c } from "react/compiler-runtime";
import { IcoMinusSmall16, IcoWarning16 } from '@onefootprint/icons';
import type { DataIdentifier } from '@onefootprint/request-types';
import type { ApiNodeDataPrerequisite, JsonSchemaObject, PrerequisiteData, PrerequisiteSourceOnboardingData, PrerequisiteSourceVault } from '@onefootprint/request-types/dashboard';
import { Button, Form, IconButton, Tooltip } from '@onefootprint/ui';
import { useEffect } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import useStore from '@/components/playbook-builder/hooks/use-store';
import { prerequisitePrefix } from '@/components/playbook-builder/utils/prerequisites';
import RequiredToggle from '@/components/required-toggle';
import type { ApplyEditMutation } from '../../../base-node';
import TypeIcon from '../../../shared/type-icon';
type PrerequisiteItem<T> = T & Omit<PrerequisiteData, 'schema'> & {
  schema?: RowType;
};
type FormData = {
  vaultData: PrerequisiteItem<PrerequisiteSourceVault>[];
  onboardingData: PrerequisiteItem<PrerequisiteSourceOnboardingData>[];
};
type PrerequisiteFormProps = {
  nodeId: string;
  node: ApiNodeDataPrerequisite;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
const parseSchema = ({
  schema: originalSchema,
  ...prereq
}: PrerequisiteData): PrerequisiteItem<PrerequisiteData> => {
  // A document is a string with a reserved format, so the format has to be read before the type.
  const type = originalSchema?.format === DOCUMENT_FORMAT ? 'document' : originalSchema?.type;
  if (!type) {
    return prereq;
  }
  if (!(type in typeOptionsMap)) {
    console.error('Unknown type for prerequisite', type, prereq);
    return prereq;
  }
  return {
    ...prereq,
    schema: type as RowType
  };
};
export const PrerequisiteForm = t0 => {
  const $ = _c(30);
  const {
    nodeId,
    node,
    mutation,
    canEdit
  } = t0;
  let t1;
  if ($[0] !== node.prerequisites) {
    t1 = node.prerequisites.map(parseSchema).filter(_temp);
    $[0] = node.prerequisites;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== node.prerequisites) {
    t2 = node.prerequisites.map(parseSchema).filter(_temp2);
    $[2] = node.prerequisites;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      vaultData: t1,
      onboardingData: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = !canEdit;
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      defaultValues: t3,
      disabled: t4
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const formMethods = useForm(t5);
  const {
    toggle
  } = useDirty();
  const {
    data: t6
  } = useStore();
  const {
    baseRequiredPrerequisites
  } = t6;
  let t7;
  if ($[10] !== formMethods.formState.isDirty || $[11] !== toggle) {
    t7 = () => {
      toggle("prerequisite-node", formMethods.formState.isDirty);
    };
    $[10] = formMethods.formState.isDirty;
    $[11] = toggle;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== formMethods.formState.isDirty) {
    t8 = [formMethods.formState.isDirty];
    $[13] = formMethods.formState.isDirty;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== mutation || $[16] !== nodeId) {
    t9 = formData => {
      const prerequisites = [...formData.vaultData, ...formData.onboardingData].map(_temp3);
      mutation.mutateAsync({
        kind: "update_node",
        nodeId,
        nodeData: {
          kind: "prerequisite",
          prerequisites
        }
      });
    };
    $[15] = mutation;
    $[16] = nodeId;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const onSubmit = t9;
  let t10;
  if ($[18] !== formMethods || $[19] !== onSubmit) {
    t10 = formMethods.handleSubmit(onSubmit);
    $[18] = formMethods;
    $[19] = onSubmit;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== baseRequiredPrerequisites || $[22] !== canEdit) {
    t11 = <FormComponent canEdit={canEdit} baseRequiredPrerequisites={baseRequiredPrerequisites} />;
    $[21] = baseRequiredPrerequisites;
    $[22] = canEdit;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <form onSubmit={t10} id="prerequisite-node">{t11}</form>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== formMethods || $[28] !== t12) {
    t13 = <FormProvider {...formMethods}>{t12}</FormProvider>;
    $[27] = formMethods;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
type FormComponentProps = {
  canEdit: boolean;
  baseRequiredPrerequisites?: string[];
};
const FormComponent = t0 => {
  const $ = _c(66);
  const {
    canEdit,
    baseRequiredPrerequisites
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "vaultData"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const vaultData = useFieldArray(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "onboardingData"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const onboardingData = useFieldArray(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "prerequisite.drawer"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    t
  } = useTranslation("playbook-builder", t3);
  let t4;
  if ($[3] !== t) {
    t4 = t(_temp4);
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <p className="text-label-2 text-primary">{t4}</p>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t) {
    t6 = t(_temp5);
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <p className="text-body-2 text-tertiary">{t6}</p>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <div className="flex flex-col gap-3"><div className="flex flex-col gap-1">{t5}{t7}</div></div>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== baseRequiredPrerequisites || $[15] !== canEdit || $[16] !== vaultData.fields || $[17] !== vaultData.remove) {
    let t10;
    if ($[19] !== baseRequiredPrerequisites || $[20] !== canEdit || $[21] !== vaultData.remove) {
      t10 = (field, index) => <Row key={field.id} field={field} index={index} onRemove={vaultData.remove} canEdit={canEdit} baseRequiredPrerequisites={baseRequiredPrerequisites} />;
      $[19] = baseRequiredPrerequisites;
      $[20] = canEdit;
      $[21] = vaultData.remove;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    t9 = vaultData.fields.map(t10);
    $[14] = baseRequiredPrerequisites;
    $[15] = canEdit;
    $[16] = vaultData.fields;
    $[17] = vaultData.remove;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[23] !== vaultData) {
    t10 = () => vaultData.append({
      kind: "vault",
      di: "" as DataIdentifier,
      schema: undefined,
      isRequired: true
    });
    $[23] = vaultData;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const t11 = !canEdit;
  let t12;
  if ($[25] !== t) {
    t12 = t(_temp6);
    $[25] = t;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t12) {
    t13 = <div><Button variant="secondary" onClick={t10} size="compact" className="pt-1" disabled={t11}>{t12}</Button></div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = t(_temp7);
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <p className="text-label-2 text-primary">{t14}</p>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t) {
    t16 = t(_temp8);
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16) {
    t17 = <p className="text-body-2 text-tertiary">{t16}</p>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t15 || $[40] !== t17) {
    t18 = <div className="flex flex-col gap-3 mt-7"><div className="flex flex-col gap-1">{t15}{t17}</div></div>;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== baseRequiredPrerequisites || $[43] !== canEdit || $[44] !== onboardingData.fields || $[45] !== onboardingData.remove) {
    let t20;
    if ($[47] !== baseRequiredPrerequisites || $[48] !== canEdit || $[49] !== onboardingData.remove) {
      t20 = (field_0, index_0) => <Row key={field_0.id} field={field_0} index={index_0} onRemove={onboardingData.remove} canEdit={canEdit} baseRequiredPrerequisites={baseRequiredPrerequisites} />;
      $[47] = baseRequiredPrerequisites;
      $[48] = canEdit;
      $[49] = onboardingData.remove;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    t19 = onboardingData.fields.map(t20);
    $[42] = baseRequiredPrerequisites;
    $[43] = canEdit;
    $[44] = onboardingData.fields;
    $[45] = onboardingData.remove;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[51] !== onboardingData) {
    t20 = () => onboardingData.append({
      kind: "onboarding_data",
      key: "",
      schema: "string",
      isRequired: true
    });
    $[51] = onboardingData;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  const t21 = !canEdit;
  let t22;
  if ($[53] !== t) {
    t22 = t(_temp9);
    $[53] = t;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t20 || $[56] !== t21 || $[57] !== t22) {
    t23 = <div><Button variant="secondary" onClick={t20} size="compact" disabled={t21} className="pt-1">{t22}</Button></div>;
    $[55] = t20;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t13 || $[60] !== t18 || $[61] !== t19 || $[62] !== t23 || $[63] !== t8 || $[64] !== t9) {
    t24 = <div className="flex flex-col gap-2">{t8}{t9}{t13}{t18}{t19}{t23}</div>;
    $[59] = t13;
    $[60] = t18;
    $[61] = t19;
    $[62] = t23;
    $[63] = t8;
    $[64] = t9;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  return t24;
};

/** Reserved `format` marking a string as a pointer to a document rather than a value to read. */
const DOCUMENT_FORMAT = 'document';
type SimpleType = Exclude<JsonSchemaObject['type'], 'array' | 'null' | 'object' | undefined>;
type RowType = SimpleType | 'document';
const typeOptionsMap: Record<RowType, JsonSchemaObject> = {
  string: {
    type: 'string'
  },
  number: {
    type: 'number'
  },
  integer: {
    type: 'integer'
  },
  boolean: {
    type: 'boolean'
  },
  document: {
    type: 'string',
    format: DOCUMENT_FORMAT
  }
};
type RowProps = {
  field: PrerequisiteItem<PrerequisiteData> & {
    id: string;
  };
  index: number;
  onRemove: (index: number) => void;
  canEdit: boolean;
  baseRequiredPrerequisites?: string[];
};
const Row = t0 => {
  const $ = _c(63);
  const {
    field,
    index,
    onRemove,
    canEdit,
    baseRequiredPrerequisites
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "prerequisite.drawer"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = Object.keys(typeOptionsMap).map(_temp10);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const typeOptions = t2;
  const fieldName = field.kind === "vault" ? `vaultData.${index}` as const : `onboardingData.${index}` as const;
  const keyName = field.kind === "vault" ? `vaultData.${index}.di` as const : `onboardingData.${index}.key` as const;
  const prefix = prerequisitePrefix[field.kind];
  let t3;
  if ($[2] !== keyName) {
    t3 = {
      name: keyName
    };
    $[2] = keyName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const key_0 = useWatch(t3);
  const t4 = `${fieldName}.isRequired`;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      name: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const isRequired = useWatch(t5);
  let t6;
  if ($[6] !== baseRequiredPrerequisites || $[7] !== isRequired || $[8] !== key_0 || $[9] !== prefix) {
    t6 = !!baseRequiredPrerequisites && !!key_0 && isRequired && !baseRequiredPrerequisites.includes(`${prefix}${key_0}`);
    $[6] = baseRequiredPrerequisites;
    $[7] = isRequired;
    $[8] = key_0;
    $[9] = prefix;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const isNewlyRequired = t6;
  const t7 = field.id;
  let t8;
  if ($[11] !== t) {
    t8 = t(_temp11);
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t(_temp12);
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = {
      required: t9
    };
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== keyName || $[18] !== prefix || $[19] !== t10 || $[20] !== t8) {
    t11 = <div className="flex-1 basis-full md:basis-0"><Form.Input name={keyName} placeholder={t8} size="compact" addon={prefix} rules={t10} /></div>;
    $[17] = keyName;
    $[18] = prefix;
    $[19] = t10;
    $[20] = t8;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const t12 = `${fieldName}.schema`;
  let t13;
  if ($[22] !== t) {
    t13 = t(_temp13);
    $[22] = t;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = field.kind === "vault";
  let t15;
  if ($[24] !== t12 || $[25] !== t13 || $[26] !== t14) {
    t15 = <div className="flex-1"><Form.Select name={t12} options={typeOptions} aria-label={t13} size="compact" disabled={t14} /></div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== isNewlyRequired || $[29] !== t) {
    t16 = isNewlyRequired && <Tooltip content={t(_temp14)}><IcoWarning16 color="warning" aria-label={t(_temp15)} className="shrink-0" /></Tooltip>;
    $[28] = isNewlyRequired;
    $[29] = t;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t15 || $[32] !== t16) {
    t17 = <div className="flex-1 flex items-center gap-2">{t15}{t16}</div>;
    $[31] = t15;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const t18 = `${fieldName}.isRequired`;
  let t19;
  if ($[34] !== field.id) {
    t19 = t20 => {
      const {
        field: t21
      } = t20;
      const {
        onChange,
        value,
        disabled
      } = t21;
      return <RequiredToggle id={field.id} value={value ? "required" : "optional"} disabled={disabled} onOptionChange={value_0 => {
        onChange(value_0 === "required");
      }} />;
    };
    $[34] = field.id;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t18 || $[37] !== t19) {
    t20 = <Controller name={t18} render={t19} />;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t) {
    t21 = t(_temp16);
    $[39] = t;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t) {
    t22 = t(_temp17);
    $[41] = t;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== index || $[44] !== onRemove) {
    t23 = () => onRemove(index);
    $[43] = index;
    $[44] = onRemove;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const t24 = !canEdit;
  let t25;
  if ($[46] !== t22 || $[47] !== t23 || $[48] !== t24) {
    t25 = <IconButton aria-label={t22} icon={IcoMinusSmall16} onClick={t23} color="secondary" disabled={t24} />;
    $[46] = t22;
    $[47] = t23;
    $[48] = t24;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  let t26;
  if ($[50] !== t21 || $[51] !== t25) {
    t26 = <Tooltip content={t21} delayOpen={1000}>{t25}</Tooltip>;
    $[50] = t21;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== t20 || $[54] !== t26) {
    t27 = <div className="flex">{t20}{t26}</div>;
    $[53] = t20;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== t11 || $[57] !== t17 || $[58] !== t27) {
    t28 = <div className="flex flex-wrap gap-2 md:flex-nowrap">{t11}{t17}{t27}</div>;
    $[56] = t11;
    $[57] = t17;
    $[58] = t27;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  let t29;
  if ($[60] !== field.id || $[61] !== t28) {
    t29 = <div key={t7} className="flex flex-col gap-2 pb-3 md:pb-0">{t28}</div>;
    $[60] = field.id;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  return t29;
};
function _temp(p) {
  return p.kind === "vault";
}
function _temp2(p_0) {
  return p_0.kind === "onboarding_data";
}
function _temp3(p_1) {
  return {
    ...p_1,
    schema: p_1.schema ? typeOptionsMap[p_1.schema] : undefined
  };
}
function _temp4($) {
  return $.vaultData.title;
}
function _temp5($_0) {
  return $_0.vaultData.description;
}
function _temp6($_1) {
  return $_1.add;
}
function _temp7($_2) {
  return $_2.onboardingData.title;
}
function _temp8($_3) {
  return $_3.onboardingData.description;
}
function _temp9($_4) {
  return $_4.add;
}
function _temp10(key) {
  return {
    label: key,
    value: key,
    component: <span className="flex items-center gap-2"><TypeIcon type={key as RowType} /> {key}</span>
  };
}
function _temp11($) {
  return $.keyPlaceholder;
}
function _temp12($_0) {
  return $_0.keyRequired;
}
function _temp13($_1) {
  return $_1.type;
}
function _temp14($_2) {
  return $_2.newRequiredField;
}
function _temp15($_3) {
  return $_3.newRequiredField;
}
function _temp16($_4) {
  return $_4.removeField;
}
function _temp17($_5) {
  return $_5.removeField;
}